// "Find in this parasha" (Tranche 4): client-side search over the verses
// ALREADY on the page. Zero network, zero route/hash changes - a calm reading
// aid, not a global search. Highlights are <mark> wraps around the original
// text nodes (the text itself is never re-rendered), removed cleanly on clear.
//
// Scope: each verse's own Hebrew (.vhe) and translation (.ven) - never source
// cards or journey chrome. Outside the replayPath selector contract entirely.

import { el, svgEl } from "../lib/dom.ts";
import { findMatches, normalizeQuery, MIN_QUERY } from "../lib/findText.ts";

const DEBOUNCE_MS = 150;

export function createFindBar(versesHost: () => HTMLElement | null): HTMLElement {
  const wrap = el("div", "rc-find");
  wrap.setAttribute("role", "group");
  wrap.setAttribute("aria-label", "Find in this parasha");

  const input = el("input", "find-input") as HTMLInputElement;
  input.type = "search";
  // id + name keep this a well-formed field (silences the "form field should
  // have an id or name" devtools issue); autocomplete off because this is a
  // calm per-session reading aid, not a form - no browser autofill wanted.
  input.id = "mm-find-in-parasha";
  input.name = "find-in-parasha";
  input.autocomplete = "off";
  input.placeholder = "Find in this parasha";
  input.setAttribute("aria-label", "Find in this parasha - Hebrew or English");

  const field = el("div", "find-field");
  const icon = svgEl("svg", {
    class: "find-icon",
    viewBox: "0 0 24 24",
    "aria-hidden": "true",
    focusable: "false",
  });
  icon.append(
    svgEl("circle", { cx: "11", cy: "11", r: "7" }),
    svgEl("path", { d: "m16.5 16.5 4.5 4.5" }),
  );
  field.append(icon, input);

  const count = el("span", "find-count", "");
  count.setAttribute("aria-live", "polite");

  const prev = el("button", "rc-btn find-prev", "Prev");
  prev.type = "button";
  prev.setAttribute("aria-label", "Previous match");
  const next = el("button", "rc-btn find-next", "Next");
  next.type = "button";
  next.setAttribute("aria-label", "Next match");

  let marks: HTMLElement[] = [];
  let cur = -1;
  let timer: ReturnType<typeof setTimeout> | null = null;

  function clearMarks(): void {
    const host = versesHost();
    for (const m of marks) m.replaceWith(...m.childNodes);
    host?.normalize(); // merge the split text nodes back together
    marks = [];
    cur = -1;
    count.textContent = "";
    prev.disabled = true;
    next.disabled = true;
  }

  // Wrap every match range in <mark>, walking the element's text nodes with a
  // running offset. Matches are processed in REVERSE document order so earlier
  // offsets stay valid while later text nodes are split.
  function highlightIn(target: HTMLElement, query: string): HTMLElement[] {
    const text = target.textContent ?? "";
    const found = findMatches(text, query);
    if (!found.length) return [];
    const walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT);
    const nodes: Array<{ node: Text; start: number }> = [];
    let off = 0;
    let n: Node | null;
    while ((n = walker.nextNode())) {
      nodes.push({ node: n as Text, start: off });
      off += (n as Text).data.length;
    }
    const made: HTMLElement[] = [];
    for (let mi = found.length - 1; mi >= 0; mi--) {
      const m = found[mi];
      for (let ni = nodes.length - 1; ni >= 0; ni--) {
        const { node, start } = nodes[ni];
        const end = start + node.data.length;
        const s = Math.max(m.start, start);
        const e = Math.min(m.end, end);
        if (s >= e) continue;
        const seg = node.splitText(s - start);
        seg.splitText(e - s);
        const mark = el("mark", "findmark");
        seg.parentNode?.replaceChild(mark, seg);
        mark.append(seg);
        made.unshift(mark); // keep document order
      }
    }
    return made;
  }

  function paintCurrent(scroll: boolean): void {
    marks.forEach((m, i) => m.classList.toggle("findmark-cur", i === cur));
    count.textContent = marks.length
      ? `${cur + 1} of ${marks.length}`
      : normalizeQuery(input.value).length >= MIN_QUERY
        ? "No matches"
        : "";
    prev.disabled = marks.length < 2;
    next.disabled = marks.length < 2;
    const m = marks[cur];
    if (scroll && m && typeof m.scrollIntoView === "function") {
      m.scrollIntoView({ block: "center" });
    }
  }

  function run(): void {
    clearMarks();
    const host = versesHost();
    if (!host) return;
    if (normalizeQuery(input.value).length < MIN_QUERY) {
      count.textContent = "";
      return;
    }
    for (const verse of host.querySelectorAll<HTMLElement>(".verse")) {
      for (const part of verse.querySelectorAll<HTMLElement>(":scope > .vhe, :scope > .ven")) {
        marks.push(...highlightIn(part, input.value));
      }
    }
    if (marks.length) cur = 0;
    paintCurrent(true);
  }

  input.addEventListener("input", () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(run, DEBOUNCE_MS);
  });
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (marks.length) {
        cur = (cur + 1) % marks.length;
        paintCurrent(true);
      }
    } else if (e.key === "Escape") {
      input.value = "";
      clearMarks();
    }
  });
  next.addEventListener("click", () => {
    if (!marks.length) return;
    cur = (cur + 1) % marks.length;
    paintCurrent(true);
  });
  prev.addEventListener("click", () => {
    if (!marks.length) return;
    cur = (cur - 1 + marks.length) % marks.length;
    paintCurrent(true);
  });

  prev.disabled = true;
  next.disabled = true;
  wrap.append(field, count, prev, next);
  return wrap;
}
